import React, { useState, useEffect, useCallback } from "react";
import {
  Pencil,
  Trash2,
  MapPin,
  Loader2,
  RefreshCw,
  Database,
  Save,
  Building2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  GitMerge,
  Link2,
  Unlink,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { TableColumnHeader } from "../../components/ui/TableColumnHeader";
import { MobileFilterBar, chip } from "../../components/ui/MobileFilterBar";
import { Badge } from "../../components/ui/Badge";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { EntitySearchPicker } from "../../components/ui/EntitySearchPicker";
import PlacePicker from "../../components/ui/PlacePicker";
import { venuesApi } from "../../utils/api";
import { useEntityDeepLink } from "../../hooks/useEntityDeepLink";
import { PreferredLabel } from "../../components/ui/PreferredLabel";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { arraysEqual, formatDate, capitalize, displayLabel, venueDisplayName, venueDocId } from "../../utils/helpers";
import { CitySelect } from "../../components/ui/CitySelect";
import {
  CLASSIFIED_FILTER_OPTIONS,
  ClassificationIssueBadge,
  ClassifyAction,
  classifiedFilterParams,
  useClassify,
} from "../../components/ui/ClassifyAction";
import { DocumentTranslations } from "../translations/DocumentTranslations";
import VenueDuplicatesView from "./VenueDuplicatesView";
import LinkRoomsDialog from "./LinkRoomsDialog";

const PAGE_SIZE = 20;

const DISPOSITION_OPTIONS = ["seated", "standing"];
const MODALITY_OPTIONS = ["indoor", "outdoor"];
const CAPACITY_BUCKET_OPTIONS = ["small", "medium", "large", "xl", "massive", "unknown"];
const WHEELCHAIR_OPTIONS = ["yes", "no", "unknown"];
// Stored values stay terse; all display wording lives here, not in the value.
const FOOD_OPTIONS = [
  { value: "nothing", label: "Nothing" },
  { value: "drinks", label: "Just drinks" },
  { value: "snacks", label: "Snacks & drinks" },
  { value: "full_meal", label: "Full meal" },
  { value: "unknown", label: "Unknown" },
];

// The API folds legacy values forward, so only the current vocabulary arrives here.
const foodLabel = (value) => FOOD_OPTIONS.find((o) => o.value === value)?.label || value || "-";

const VENUE_SORT_COLUMNS = [
  { key: "upcoming_event_count", label: "Upcoming events" },
  { key: "first_seen", label: "First Seen" },
];

const VENUE_TABLE_COLUMNS = [
  { key: "upcoming_event_count", label: "Upcoming events", sortable: true },
  { key: "capacity_bucket", label: "Capacity" },
  { key: "venue_type_hint", label: "Type hint" },
  { key: "disposition", label: "Disposition" },
  { key: "modality", label: "Modality" },
  { key: "first_seen", label: "First Seen", sortable: true },
];

// Sentinel understood by GET /admin/venues?main_population= — venues that have no value.
const UNASSIGNED_MAIN_POPULATION = "__unassigned__";

// Shared between the desktop in-column filter dropdown and the mobile segmented control.
// A venue has no themes, so "No themes" is not a venue issue.
const VENUE_CLASSIFIED_FILTER_OPTIONS = CLASSIFIED_FILTER_OPTIONS.filter((option) => option.value !== "no_themes");
// "Classified" for a venue is a non-empty `disposition` — what the list filter's `exists` and the
// pipeline both test, so an empty one (an admin unticking every box) is re-classified next run.
const isVenueClassified = (venue) => Boolean(venue?.disposition?.length);
// The badge also flags a venue the classifier gave up on.
const VenueClassifiedBadge = ({ venue, labels }) => (
  <ClassificationIssueBadge
    classified={isVenueClassified(venue)}
    meta={venue.enrichment_metadata}
    enricher="venue_llm"
    labels={labels}
  />
);
const VERIFIED_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Verified" },
  { value: "false", label: "Unverified" },
];
// GET /admin/venues?venue_role= — "none" is a venue in no room group.
const ROLE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "main", label: "Main venue" },
  { value: "room", label: "Room" },
  { value: "none", label: "Neither" },
];
const getRoleBadge = (role) =>
  role === "main" ? (
    <Badge variant="default">Main venue</Badge>
  ) : role === "room" ? (
    <Badge variant="secondary">Room</Badge>
  ) : (
    <span className="text-xs text-muted-foreground">-</span>
  );

// Derives capacity bucket from server-fetched thresholds.
// buckets: [{name, min, max, label}] ordered from smallest to largest.
function inferCapacityBucketFromThresholds(capacity, buckets) {
  const n = parseInt(capacity, 10);
  if (!capacity || isNaN(n) || n <= 0) return "";
  for (const b of buckets) {
    if (b.name === "unknown") continue;
    if (b.max === null || n < b.max) return b.name;
  }
  return "massive";
}

const getCapacityBucketBadge = (bucket) => {
  const colors = {
    small: "secondary",
    medium: "outline",
    large: "default",
    xl: "warning",
    massive: "destructive",
    unknown: "outline",
  };
  return <Badge variant={colors[bucket] || "outline"}>{bucket}</Badge>;
};

const getModalityBadge = (m) => {
  const colors = { indoor: "secondary", outdoor: "success" };
  return <Badge variant={colors[m] || "outline"}>{m}</Badge>;
};

const getDispositionBadge = (d) => (
  <Badge variant="outline">{d}</Badge>
);

// Rendered inside the table body (not in place of the table) so the column
// filters that produced the empty result stay on screen and can be undone.
const NoVenues = () => (
  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
    <Database className="h-12 w-12 mb-4" />
    <p className="text-lg font-medium">No venues found</p>
  </div>
);

const getVerifiedBadge = (verified) => (
  <Badge variant={verified ? "success" : "outline"}>{verified ? "Verified" : "Unverified"}</Badge>
);

const renderMultiBadges = (values, renderer) => {
  if (!Array.isArray(values) || values.length === 0) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((v) => <React.Fragment key={v}>{renderer(v)}</React.Fragment>)}
    </div>
  );
};

const toggleInList = (list, value) => {
  if (!Array.isArray(list)) return [value];
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
};

// The venue picker shared by "Add duplicate" and "Attach to venue…". The admin venue list
// already excludes status=duplicate, so no venue the API would refuse can be picked.
const venuePickerProps = {
  allowRawId: false,
  emptyLabel: "No venue matches that name or ID.",
  // Picked by document id, what merge-into and link read by.
  search: (query, config) =>
    venuesApi
      .getAll({ search: query, page_size: 8 }, config)
      .then((data) => (data.venues || []).map((venue) => ({ ...venue, id: venueDocId(venue) }))),
  optionLabel: venueDisplayName,
  optionSublabel: (venue) => [venue.city, venue.country].filter(Boolean).join(" · "),
};

// A room's or main's name as a link that opens that venue's detail. Only the id travels on the
// venue document, so the name is read on demand.
function RoomLink({ venueId, onOpen }) {
  const [venue, setVenue] = useState(null);
  useEffect(() => {
    let cancelled = false;
    venuesApi
      .getById(venueId)
      // Room links are document ids; keep it on the venue so the detail's actions send it.
      .then((data) => !cancelled && setVenue({ ...(data?.venue ?? data), _id: venueId }))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [venueId]);
  return (
    <div>
      <button
        type="button"
        className="text-primary hover:underline disabled:no-underline"
        disabled={!venue}
        onClick={() => onOpen(venue)}
      >
        {venue ? displayLabel(venue.preferred_name, venue.name) : venueId}
      </button>
      {venue && <ProviderLabel preferred={venue.preferred_name} original={venue.name} />}
    </div>
  );
}

export default function VenuesPage() {
  const [venues, setVenues] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const { searchInput, setSearchInput, search: searchFilter, submitSearch, clearSearch } = useSearchFilter();
  const [cityFilter, setCityFilter] = useState("");
  // "" = no filter, a city name, or UNASSIGNED_MAIN_POPULATION for venues with no value
  // (no coordinates, or outside every active radius).
  const [mainPopulationFilter, setMainPopulationFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState(""); // "", "true", "false"
  // Classified = has a `venue_llm` classification (`disposition`); see VENUE_CLASSIFIED_FILTER_OPTIONS.
  const [classifiedFilter, setClassifiedFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState(""); // "", "main", "room", "none"
  const [capacityBuckets, setCapacityBuckets] = useState([]);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Classify / Re-classify (venue_llm) on the detail modal: one synchronous LLM call.
  const classify = useClassify(venuesApi.classify);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Manual duplicate: pick a venue to fold into `selectedVenue` (the canonical).
  const [isMarkDuplicateOpen, setIsMarkDuplicateOpen] = useState(false);
  const [dupPicked, setDupPicked] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupError, setDupError] = useState(null);

  // Rooms: attach the open venue to another one, or detach it from its main.
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [attachPicked, setAttachPicked] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [isDetachOpen, setIsDetachOpen] = useState(false);
  const [detachBusy, setDetachBusy] = useState(false);
  const [detachError, setDetachError] = useState(null);

  // Top-left tab: venues table vs. duplicate review queue
  const [activeTab, setActiveTab] = useState("venues");
  const [venueDuplicateCount, setVenueDuplicateCount] = useState(0);

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (searchFilter) params.search = searchFilter;
      if (cityFilter) params.city = cityFilter;
      if (mainPopulationFilter) params.main_population = mainPopulationFilter;
      if (verifiedFilter !== "") params.verified = verifiedFilter === "true";
      Object.assign(params, classifiedFilterParams(classifiedFilter));
      if (roleFilter) params.venue_role = roleFilter;
      if (sortKey) {
        params.sort_by = sortKey;
        params.sort_order = sortDir;
      }

      const data = await venuesApi.getAll(params);
      setVenues(data.venues || []);
      setPagination(data.pagination || null);
    } catch (err) {
      console.error("Error fetching venues:", err);
      if (!err.response) {
        setError("Could not connect to the server.");
      } else {
        setError(err.response?.data?.detail || "Error loading venues");
      }
    } finally {
      setLoading(false);
    }
  }, [page, searchFilter, cityFilter, mainPopulationFilter, verifiedFilter, classifiedFilter, roleFilter, sortKey, sortDir]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  // Reloads the "Duplicates (N)" tab badge — on mount, so it is accurate before the tab is ever
  // opened, and after a manual merge, which closes every open pair referencing the merged venue.
  const refreshDuplicateQueue = useCallback(() => {
    venuesApi
      .getDuplicates(1, 1)
      .then((d) => setVenueDuplicateCount(d.total_groups || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshDuplicateQueue();
  }, [refreshDuplicateQueue]);

  useEffect(() => {
    let cancelled = false;
    venuesApi.getCapacityBuckets().then((buckets) => {
      if (!cancelled) setCapacityBuckets(buckets);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);


  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const venueMatchesVerifiedFilter = useCallback(
    (verified) => verifiedFilter === "" || verifiedFilter === String(Boolean(verified)),
    [verifiedFilter]
  );

  const applyVenueListUpdate = useCallback(
    (venueId, updates) => {
      const hasVerifiedUpdate = Object.prototype.hasOwnProperty.call(updates, "verified");
      const shouldKeep = !hasVerifiedUpdate || venueMatchesVerifiedFilter(updates.verified);

      setVenues((prev) =>
        shouldKeep
          ? prev.map((v) => (venueDocId(v) === venueId ? { ...v, ...updates } : v))
          : prev.filter((v) => venueDocId(v) !== venueId)
      );

      if (!shouldKeep) {
        setPagination((prev) => {
          if (!prev) return prev;
          const total = Math.max(0, (prev.total || 0) - 1);
          return {
            ...prev,
            total,
            total_pages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
          };
        });
      }
    },
    [venueMatchesVerifiedFilter]
  );

  const handleSearch = (e) => {
    submitSearch(e);
    setPage(1);
  };

  const handleCityChange = (value) => {
    setCityFilter(value);
    setPage(1);
  };

  const handleMainPopulationChange = (value) => {
    setMainPopulationFilter(value);
    setPage(1);
  };

  const handleSort = (key) => {
    const newDir = sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(key);
    setSortDir(newDir);
    setPage(1);
  };

  // Rendered as a TableColumnHeader dropdown on desktop, by MobileFilterBar below `lg`.
  const verifiedColumnFilter = {
    value: verifiedFilter,
    options: VERIFIED_FILTER_OPTIONS,
    onChange: (value) => {
      setVerifiedFilter(value);
      setPage(1);
    },
  };

  const classifiedColumnFilter = {
    value: classifiedFilter,
    options: VENUE_CLASSIFIED_FILTER_OPTIONS,
    onChange: (value) => {
      setClassifiedFilter(value);
      setPage(1);
    },
  };

  const roleColumnFilter = {
    value: roleFilter,
    options: ROLE_FILTER_OPTIONS,
    onChange: (value) => {
      setRoleFilter(value);
      setPage(1);
    },
  };

  const handleClearSearch = () => {
    clearSearch();
    setCityFilter("");
    setMainPopulationFilter("");
    setVerifiedFilter("");
    setClassifiedFilter("");
    setRoleFilter("");
    setPage(1);
  };

  const handleClassify = async () => {
    const venue = selectedVenue;
    if (
      isVenueClassified(venue) &&
      !window.confirm(
        "Re-classify this venue? The classifier's new result replaces disposition, modality, capacity, accessibility and the rest (one LLM call). If it fails, the current values are kept."
      )
    )
      return;
    const docId = venueDocId(venue);
    await classify.run(docId);
    fetchVenues();
    try {
      const data = await venuesApi.getById(docId);
      const loaded = data?.venue ?? data;
      setSelectedVenue((current) => (venueDocId(current) === docId ? { ...current, ...loaded } : current));
    } catch (err) {
      console.error("Error reloading venue:", err);
    }
  };

  const handleOpenDetail = (venue) => {
    setSelectedVenue(venue);
    setIsDetailModalOpen(true);
  };

  // Deep link: `?venue=<id>` (any EntityLink to it) opens that venue's
  // detail modal on load.
  useEntityDeepLink("venue", async (venueId) => {
    try {
      const data = await venuesApi.getById(venueId);
      // GET /admin/venues/{id} wraps the venue as { venue: {...} } and opens it by either id (an
      // event links its venue by `_source.id`); its `_id` is the document id every action needs.
      const venue = data?.venue ?? data;
      handleOpenDetail({ ...venue, _id: venue?._id || venueId });
    } catch (err) {
      console.error("Error opening venue from deep link:", err);
      setError(err.response?.data?.detail || "Could not open the requested venue");
    }
  });

  const handleOpenEdit = (venue) => {
    setSelectedVenue(venue);
    setEditData({
      preferred_name: venue.preferred_name || "",
      url: venue.url || "",
      google_place_id: venue.google_place_id || "",
      address: venue.address || "",
      city: venue.city || "",
      country: venue.country || "",
      state: venue.state || "",
      main_population: venue.main_population || "",
      disposition: Array.isArray(venue.disposition) ? [...venue.disposition] : [],
      modality: Array.isArray(venue.modality) ? [...venue.modality] : [],
      capacity_bucket: venue.capacity_bucket || "",
      capacity: venue.capacity ?? "",
      venue_type_hint: venue.venue_type_hint || "",
      wheelchair_accessible: venue.wheelchair_accessible || "",
      public_transport_access: venue.public_transport_access || "",
      food_and_drinks: venue.food_and_drinks || "",
      venue_inferred_context: venue.venue_inferred_context || "",
      latitude: venue.latitude ?? "",
      longitude: venue.longitude ?? "",
      verified: !!venue.verified,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedVenue) return;
    setIsSaving(true);
    setError(null);
    try {
      const updatePayload = {};

      const textFields = [
        // Blank clears it (sent as null below) and the venue falls back to its provider name.
        "preferred_name",
        "url",
        // Clearing it sends null, which unlinks the venue — the loop below already does that.
        "google_place_id",
        "address",
        "city",
        "country",
        "state",
        "main_population",
        "capacity_bucket",
        "venue_type_hint",
        "wheelchair_accessible",
        "public_transport_access",
        "food_and_drinks",
        "venue_inferred_context",
      ];
      for (const field of textFields) {
        const original = selectedVenue[field] ?? "";
        if ((editData[field] ?? "") !== original) {
          updatePayload[field] = editData[field] === "" ? null : editData[field];
        }
      }

      const origDisposition = Array.isArray(selectedVenue.disposition) ? selectedVenue.disposition : [];
      if (!arraysEqual([...editData.disposition].sort(), [...origDisposition].sort())) {
        updatePayload.disposition = editData.disposition;
      }
      const origModality = Array.isArray(selectedVenue.modality) ? selectedVenue.modality : [];
      if (!arraysEqual([...editData.modality].sort(), [...origModality].sort())) {
        updatePayload.modality = editData.modality;
      }

      if (editData.capacity !== (selectedVenue.capacity ?? "")) {
        if (editData.capacity === "" || editData.capacity === null) {
          updatePayload.capacity = null;
        } else {
          const cap = parseInt(editData.capacity, 10);
          if (!isNaN(cap) && cap >= 0) updatePayload.capacity = cap;
        }
      }

      for (const field of ["latitude", "longitude"]) {
        const original = selectedVenue[field] ?? "";
        if (editData[field] === original) continue;
        if (editData[field] === "") {
          // Only a generated main's coordinates can be cleared: they then follow its rooms again.
          if (selectedVenue.main_venue_generated) updatePayload[field] = null;
          continue;
        }
        const value = parseFloat(editData[field]);
        if (!isNaN(value)) updatePayload[field] = value;
      }

      if (editData.verified !== !!selectedVenue.verified) {
        updatePayload.verified = editData.verified;
      }

      if (Object.keys(updatePayload).length === 0) {
        setIsEditModalOpen(false);
        return;
      }

      const result = await venuesApi.update(venueDocId(selectedVenue), updatePayload);
      setSuccess(`Venue updated: ${result.updated_fields?.join(", ") || "OK"}`);

      if (selectedVenue.main_venue_generated) {
        // The API also records which fields are now set here and may recompute cleared ones from the
        // rooms, none of which the payload carries.
        fetchVenues();
      } else {
        // Optimistically merge the saved fields while preserving active filters.
        applyVenueListUpdate(venueDocId(selectedVenue), updatePayload);
      }

      setIsEditModalOpen(false);
      setSelectedVenue(null);
    } catch (err) {
      console.error("Error updating venue:", err);
      setError(err.response?.data?.detail || "Error updating venue");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = (venue) => {
    setSelectedVenue(venue);
    setIsDeleteModalOpen(true);
  };

  const handleToggleVerified = async (venueId, verified) => {
    setIsVerifying(true);
    setError(null);
    try {
      await venuesApi.verify(venueId, verified);
      setSuccess(`Venue ${verified ? "verified" : "unverified"}`);
      applyVenueListUpdate(venueId, { verified });
      setSelectedVenue((prev) => (prev && venueDocId(prev) === venueId ? { ...prev, verified } : prev));
    } catch (err) {
      console.error("Error updating verified status:", err);
      setError(err.response?.data?.detail || "Error updating verified status");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDelete = async (force = false) => {
    if (!selectedVenue) return;
    setIsDeleting(true);
    setError(null);
    try {
      await venuesApi.delete(venueDocId(selectedVenue), force);
      setSuccess("Venue deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedVenue(null);
      fetchVenues();
    } catch (err) {
      console.error("Error deleting venue:", err);
      const detail = err.response?.data?.detail;
      // Two refusals share the 409: events still reference the venue (the detail carries their
      // count), which Force delete overrides, or it is in a room group, which it does not.
      if (err.response?.status === 409 && detail?.affected_events) {
        setError(`This venue is referenced by ${detail.affected_events} events. Use 'Force delete'.`);
      } else {
        setError((typeof detail === "string" ? detail : detail?.message) || "Error deleting venue");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenMarkDuplicate = () => {
    setDupPicked(null);
    setDupError(null);
    setIsMarkDuplicateOpen(true);
  };

  const handleMarkDuplicate = async () => {
    if (!selectedVenue || !dupPicked) return;
    setDupBusy(true);
    setDupError(null);
    try {
      // The picked venue is folded in; the open venue stays canonical.
      const result = await venuesApi.mergeInto(dupPicked.id, venueDocId(selectedVenue));
      setSuccess(
        `"${dupPicked.label || dupPicked.id}" merged into "${venueDisplayName(selectedVenue)}"` +
          (result?.room_group_warning ? `. Warning: ${result.room_group_warning}` : "")
      );
      setIsMarkDuplicateOpen(false);
      setIsDetailModalOpen(false);
      setSelectedVenue(null);
      // The merged venue leaves the list (must_not status=duplicate) and the canonical may have
      // absorbed source ids, names and missing fields, so re-read rather than patching in place.
      fetchVenues();
      refreshDuplicateQueue();
    } catch (err) {
      setDupError(err.response?.data?.detail || "Could not merge venue");
    } finally {
      setDupBusy(false);
    }
  };

  const handleLinked = async () => {
    setLinkOpen(false);
    setIsAttachOpen(false);
    setIsDetailModalOpen(false);
    setSelectedVenue(null);
    setSuccess("Linked as rooms");
    fetchVenues();
    refreshDuplicateQueue();
  };

  const handleDetach = async () => {
    const venue = selectedVenue;
    const docId = venueDocId(venue);
    setDetachBusy(true);
    setDetachError(null);
    try {
      await venuesApi.detach(docId);
    } catch (err) {
      setDetachError(err.response?.data?.detail || "Could not detach the venue");
      return;
    } finally {
      setDetachBusy(false);
    }
    setIsDetachOpen(false);
    setSuccess(`"${venueDisplayName(venue)}" detached`);
    fetchVenues();
    try {
      const data = await venuesApi.getById(docId);
      const loaded = { ...(data?.venue ?? data), _id: docId };
      setSelectedVenue((current) => (venueDocId(current) === docId ? loaded : current));
    } catch (err) {
      console.error("Error reloading the detached venue:", err);
    }
  };

  const totalPages = pagination?.total_pages || 1;
  const totalVenues = pagination?.total || 0;

  const inputClass =
    "mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  return (
    <div className="space-y-6">
      {/* Top-left tabs: Venues table vs. duplicate review queue */}
      <div className="flex items-center gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab("venues")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === "venues"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Venues
        </button>
        <button
          onClick={() => setActiveTab("duplicates")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === "duplicates"
              ? "border-primary text-foreground"
              : venueDuplicateCount > 0
              ? "border-transparent text-yellow-700 hover:text-yellow-800"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Duplicates ({venueDuplicateCount})
        </button>
      </div>

      {activeTab === "duplicates" && <VenueDuplicatesView onQueueChange={setVenueDuplicateCount} />}

      {activeTab === "venues" && (<>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Venues</h2>
          <p className="text-muted-foreground">Manage platform venues</p>
        </div>
        <Button variant="outline" onClick={fetchVenues} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Close</button>
        </div>
      )}
      {success && (
        <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
          {success}
          <button className="ml-2 underline" onClick={() => setSuccess(null)}>Close</button>
        </div>
      )}

      {/* Stats */}
      <Card>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-2xl font-bold">{totalVenues}</div>
              <p className="text-xs text-muted-foreground">Total venues</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {searchFilter && <Badge variant="outline" className="text-sm">Search: "{searchFilter}"</Badge>}
              {cityFilter && <Badge variant="secondary" className="text-sm">City: {capitalize(cityFilter)}</Badge>}
              {mainPopulationFilter && (
                <Badge variant="secondary" className="text-sm">
                  Main population: {mainPopulationFilter === UNASSIGNED_MAIN_POPULATION ? "Unassigned" : capitalize(mainPopulationFilter)}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search & Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <SearchFilterForm
              value={searchInput}
              onChange={setSearchInput}
              onSubmit={handleSearch}
              onClear={handleClearSearch}
              placeholder="Search by name or venue ID"
              loading={loading}
              inputClassName="sm:w-64"
            />
            <CitySelect value={cityFilter} onChange={handleCityChange} />
            <CitySelect
              value={mainPopulationFilter}
              onChange={handleMainPopulationChange}
              allLabel="All main populations"
              extraOptions={[{ value: UNASSIGNED_MAIN_POPULATION, label: "Unassigned" }]}
              widthClass="w-52"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading venues...</span>
            </div>
          ) : (
            <>
              <div className="border-b border-border p-3 space-y-3 lg:hidden">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground shrink-0">Sort</span>
                  <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                    {VENUE_SORT_COLUMNS.map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleSort(key)}
                        className={chip(sortKey === key)}
                      >
                        {label}
                        {sortKey === key ? (
                          sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <MobileFilterBar
                  filters={[
                    { key: "verified", label: "Verified", ...verifiedColumnFilter },
                    { key: "classified", label: "Classified", ...classifiedColumnFilter },
                    { key: "role", label: "Role", ...roleColumnFilter },
                  ]}
                />
              </div>
              <div className="divide-y divide-border lg:hidden">
                {venues.length === 0 && <NoVenues />}
                {venues.map((venue) => (
                  <div
                    key={venue.id}
                    className="space-y-3 p-4"
                    onClick={() => handleOpenDetail(venue)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleOpenDetail(venue)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{venueDisplayName(venue)}</p>
                        <ProviderLabel preferred={venue.preferred_name} original={venue.name} />
                        {venue.address && (
                          <p className="text-sm text-muted-foreground">{venue.address}</p>
                        )}
                        <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>{venue.city || "-"}</span>
                          {venue.country && <span>/ {venue.country}</span>}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          {getVerifiedBadge(venue.verified)}
                          <VenueClassifiedBadge venue={venue} labels={["Classified", "Unclassified"]} />
                          {venue.venue_role && getRoleBadge(venue.venue_role)}
                          <span className="text-xs text-muted-foreground">
                            {venue.upcoming_event_count ?? 0} upcoming
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Capacity</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {venue.capacity_bucket
                            ? getCapacityBucketBadge(venue.capacity_bucket)
                            : <span className="text-xs text-muted-foreground">-</span>}
                          {venue.capacity != null && (
                            <span className="text-xs text-muted-foreground">{venue.capacity.toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Type hint</p>
                        <div className="mt-1">
                          {venue.venue_type_hint
                            ? <Badge variant="outline" className="text-xs">{venue.venue_type_hint}</Badge>
                            : <span className="text-xs text-muted-foreground">-</span>}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Disposition</p>
                        <div className="mt-1">{renderMultiBadges(venue.disposition, getDispositionBadge)}</div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Modality</p>
                        <div className="mt-1">{renderMultiBadges(venue.modality, getModalityBadge)}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        First seen {formatDate(venue.first_seen)}
                      </span>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(venue)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(venue)} title="Delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block">
                <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Venue</TableHead>
                  <TableHead>City</TableHead>
                  <TableColumnHeader label="Verified" filter={verifiedColumnFilter} />
                  <TableColumnHeader label="Classified" filter={classifiedColumnFilter} />
                  <TableColumnHeader label="Role" filter={roleColumnFilter} />
                  {VENUE_TABLE_COLUMNS.map(({ key, label, sortable }) => (
                    <TableHead key={key}>
                      {sortable ? (
                        <button
                          onClick={() => handleSort(key)}
                          className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded whitespace-nowrap"
                        >
                          {label}
                          {sortKey === key ? (
                            sortDir === "asc" ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowDown className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
                          )}
                        </button>
                      ) : (
                        <span className="whitespace-nowrap">{label}</span>
                      )}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {venues.length === 0 && (
                  <TableRow>
                    {/* Venue + City + Verified + Classified + Role + sortable columns + Actions */}
                    <TableCell colSpan={VENUE_TABLE_COLUMNS.length + 6}>
                      <NoVenues />
                    </TableCell>
                  </TableRow>
                )}
                {venues.map((venue) => (
                  <TableRow
                    key={venue.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleOpenDetail(venue)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[200px]">{venueDisplayName(venue)}</p>
                          <ProviderLabel preferred={venue.preferred_name} original={venue.name} className="truncate max-w-[200px]" />
                          {venue.address && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {venue.address}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{venue.city || "-"}</span>
                      </div>
                      {venue.country && (
                        <span className="text-xs text-muted-foreground">{venue.country}</span>
                      )}
                    </TableCell>
                    <TableCell>{getVerifiedBadge(venue.verified)}</TableCell>
                    <TableCell>
                      <VenueClassifiedBadge venue={venue} labels={["Yes", "No"]} />
                    </TableCell>
                    <TableCell>{getRoleBadge(venue.venue_role)}</TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">{venue.upcoming_event_count ?? "-"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {venue.capacity_bucket
                          ? getCapacityBucketBadge(venue.capacity_bucket)
                          : <span className="text-xs text-muted-foreground">-</span>}
                        {venue.capacity != null && (
                          <span className="text-xs text-muted-foreground">{venue.capacity.toLocaleString()}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {venue.venue_type_hint
                        ? <Badge variant="outline" className="text-xs">{venue.venue_type_hint}</Badge>
                        : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>{renderMultiBadges(venue.disposition, getDispositionBadge)}</TableCell>
                    <TableCell>{renderMultiBadges(venue.modality, getModalityBadge)}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(venue.first_seen)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(venue)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(venue)} title="Delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Detail Modal */}
      <Modal open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <ModalContent title="Venue Details" className="max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]">
          {selectedVenue && (
            <div className="space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedVenue.name}</h3>
                  <PreferredLabel label="Preferred name" value={selectedVenue.preferred_name} original={selectedVenue.name} />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {selectedVenue.city || "-"}, {selectedVenue.country || "-"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="venue-verified-toggle"
                  checked={!!selectedVenue.verified}
                  disabled={isVerifying}
                  onChange={(e) => handleToggleVerified(venueDocId(selectedVenue), e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="venue-verified-toggle" className="text-sm font-medium">
                  Verified
                </label>
                {isVerifying && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </div>

              {/* A generated main is built from its rooms and never classified. */}
              {!selectedVenue.main_venue_generated && (
              <ClassifyAction
                busy={classify.isBusy(venueDocId(selectedVenue))}
                result={classify.resultFor(venueDocId(selectedVenue))}
                isReclassify={isVenueClassified(selectedVenue)}
                onClick={handleClassify}
                renderPrevious={(r) =>
                  r.previous_classification && (
                    <dl className="grid gap-1 text-xs sm:grid-cols-2">
                      {Object.entries(r.previous_classification).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-muted-foreground">{key}</dt>
                          <dd>{Array.isArray(value) ? value.join(", ") : value == null ? "-" : typeof value === "object" ? JSON.stringify(value) : String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  )
                }
              >
                <VenueClassifiedBadge venue={selectedVenue} />
              </ClassifyAction>
              )}

              {selectedVenue.venue_role === "main" && (selectedVenue.room_ids || []).length > 0 && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Main venue of:</p>
                  <ul className="list-disc pl-5">
                    {selectedVenue.room_ids.map((id) => (
                      <li key={id}>
                        <RoomLink venueId={id} onOpen={handleOpenDetail} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedVenue.venue_role === "room" && selectedVenue.main_venue_id && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-muted-foreground">Room of:</span>
                  <RoomLink venueId={selectedVenue.main_venue_id} onOpen={handleOpenDetail} />
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={() => {
                      setDetachError(null);
                      setIsDetachOpen(true);
                    }}
                  >
                    <Unlink className="mr-2 h-4 w-4" /> Detach
                  </Button>
                </div>
              )}

              <div className="grid gap-4 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground">ID</p>
                  <p className="font-mono text-xs truncate">{selectedVenue.id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Address</p>
                  <p>{selectedVenue.address || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Country / State</p>
                  <p>{selectedVenue.country || "-"} {selectedVenue.state ? `/ ${selectedVenue.state}` : ""}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Main population</p>
                  <p>{selectedVenue.main_population ? capitalize(selectedVenue.main_population) : "Unassigned"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">First seen</p>
                  <p>{formatDate(selectedVenue.first_seen)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Capacity bucket</p>
                  <div>{selectedVenue.capacity_bucket ? getCapacityBucketBadge(selectedVenue.capacity_bucket) : "-"}</div>
                </div>
                <div>
                  <p className="text-muted-foreground">Capacity</p>
                  <p>{selectedVenue.capacity != null ? selectedVenue.capacity.toLocaleString() : "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Venue type hint</p>
                  <p>{selectedVenue.venue_type_hint || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Wheelchair accessible</p>
                  <p>{selectedVenue.wheelchair_accessible || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Disposition</p>
                  <div>{renderMultiBadges(selectedVenue.disposition, getDispositionBadge)}</div>
                </div>
                <div>
                  <p className="text-muted-foreground">Modality</p>
                  <div>{renderMultiBadges(selectedVenue.modality, getModalityBadge)}</div>
                </div>
                <div>
                  <p className="text-muted-foreground">Food & drinks</p>
                  <p>{foodLabel(selectedVenue.food_and_drinks)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Public transport</p>
                  <p className="text-xs">{selectedVenue.public_transport_access || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Latitude</p>
                  <p className="font-mono text-xs">{selectedVenue.latitude ?? "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Longitude</p>
                  <p className="font-mono text-xs">{selectedVenue.longitude ?? "-"}</p>
                </div>
                {selectedVenue.venue_inferred_context && (
                  <div className="sm:col-span-2">
                    <p className="text-muted-foreground">Inferred context</p>
                    <p className="text-xs whitespace-pre-wrap">{selectedVenue.venue_inferred_context}</p>
                  </div>
                )}
                {selectedVenue.url && (
                  <div className="sm:col-span-2">
                    <p className="text-muted-foreground">URL</p>
                    <a href={selectedVenue.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs truncate block">
                      {selectedVenue.url}
                    </a>
                  </div>
                )}
              </div>

              {selectedVenue.source_ids && Object.keys(selectedVenue.source_ids).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Source IDs</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {Object.entries(selectedVenue.source_ids).map(([k, v]) => (
                      <Badge key={k} variant="secondary">{k}: {v}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <DocumentTranslations key={venueDocId(selectedVenue)} type="venue" id={venueDocId(selectedVenue)} />

              {selectedVenue.event_count != null && (
                <div>
                  <p className="text-muted-foreground text-sm">Associated events</p>
                  <p className="text-lg font-bold">{selectedVenue.event_count}</p>
                </div>
              )}

              <ModalFooter className="border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isVerifying}
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    handleOpenEdit(selectedVenue);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                {/* A duplicate can't be a canonical — the API rejects a merge on either side of
                    the pair. Only offer the action where it means what it says. The venue list
                    excludes duplicates, so this only bites on the `?venue=<id>` deep link. */}
                {selectedVenue.status !== "duplicate" && (
                  <Button variant="outline" size="sm" onClick={handleOpenMarkDuplicate}>
                    <GitMerge className="h-4 w-4 mr-2" /> Add duplicate
                  </Button>
                )}
                {selectedVenue.status !== "duplicate" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAttachPicked(null);
                      setIsAttachOpen(true);
                    }}
                  >
                    <Link2 className="h-4 w-4 mr-2" /> Attach to venue…
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); setSelectedVenue(null); }}>
                  Close
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Manual duplicate: fold another venue into the open (canonical) one */}
      <Modal open={isMarkDuplicateOpen} onOpenChange={(open) => { if (!dupBusy) setIsMarkDuplicateOpen(open); }}>
        <ModalContent title="Add duplicate" className="max-w-lg">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Canonical: <span className="font-medium text-foreground">{venueDisplayName(selectedVenue)}</span>.
              The venue you pick is merged into it — its events are repointed, its source ids, name
              history and any field the canonical is missing are absorbed, and it is retained as a
              duplicate pointing here.
            </p>

            <EntitySearchPicker
              id="mark_duplicate_venue_search"
              label="Duplicate venue"
              required
              placeholder="Search venues by name or preferred name, or paste a venue ID..."
              value={dupPicked}
              onChange={setDupPicked}
              disabled={dupBusy}
              excludeId={venueDocId(selectedVenue)}
              {...venuePickerProps}
            />

            {dupError && <p className="text-sm text-destructive">{dupError}</p>}
          </div>

          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsMarkDuplicateOpen(false)} disabled={dupBusy}>
              Cancel
            </Button>
            <Button onClick={handleMarkDuplicate} disabled={!dupPicked || dupBusy}>
              {dupBusy ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging...</>
              ) : (
                <><GitMerge className="h-4 w-4 mr-2" />Merge into this venue</>
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Attach to another venue as rooms of one place */}
      <Modal open={isAttachOpen && !linkOpen} onOpenChange={setIsAttachOpen}>
        <ModalContent title="Attach to venue" className="max-w-lg">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Pick the venue that <span className="font-medium text-foreground">{venueDisplayName(selectedVenue)}</span>{" "}
              shares a place with. You choose the roles on the next step.
            </p>
            <EntitySearchPicker
              id="attach_venue_search"
              label="Other venue"
              required
              placeholder="Search venues by name or preferred name..."
              value={attachPicked}
              onChange={setAttachPicked}
              excludeId={venueDocId(selectedVenue)}
              {...venuePickerProps}
            />
          </div>
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsAttachOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setLinkOpen(true)} disabled={!attachPicked}>
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <LinkRoomsDialog
        open={linkOpen}
        venueA={selectedVenue}
        venueB={attachPicked && { id: attachPicked.id, name: attachPicked.label || attachPicked.id }}
        onClose={() => setLinkOpen(false)}
        onLinked={handleLinked}
      />

      <Modal open={isDetachOpen} onOpenChange={(open) => { if (!detachBusy) setIsDetachOpen(open); }}>
        <ModalContent title="Detach from main venue" className="max-w-lg">
          <p className="text-sm text-muted-foreground">
            {venueDisplayName(selectedVenue)} stops being a room. If its main venue was generated from
            its rooms, the main is recomputed, or dissolved when no rooms remain.
          </p>
          {detachError && <p className="mt-3 text-sm text-destructive">{detachError}</p>}
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsDetachOpen(false)} disabled={detachBusy}>
              Cancel
            </Button>
            <Button onClick={handleDetach} disabled={detachBusy}>
              {detachBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Detach
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <ModalContent title="Edit Venue" className="max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]">
          {selectedVenue && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground mb-2">
                <strong>{selectedVenue.name}</strong> (provider name, not editable)
              </div>
              {selectedVenue.main_venue_generated && (
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>
                    This main venue is built from its rooms: its address, coordinates, city, country and
                    main population follow them until you set one here, which then stays as you set it.
                    Clear a field to let its rooms decide it again. Its Google place is also shown for
                    each room that has none of its own.
                  </p>
                  <p>
                    Set here:{" "}
                    {(selectedVenue.main_locked_fields || []).length
                      ? selectedVenue.main_locked_fields.map((field) => field.replace("_", " ")).join(", ")
                      : "nothing yet"}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Preferred name</label>
                <input
                  type="text"
                  value={editData.preferred_name}
                  onChange={(e) => setEditData((d) => ({ ...d, preferred_name: e.target.value }))}
                  placeholder={selectedVenue.name || ""}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Shown to users instead of the provider name — e.g. &ldquo;Sala Apolo&rdquo; for
                  &ldquo;Sala Apolo (Sala 1)&rdquo;. Also compared by duplicate detection. Leave empty to use the
                  provider name.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-verified"
                  checked={editData.verified}
                  onChange={(e) => setEditData((d) => ({ ...d, verified: e.target.checked }))}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="edit-verified" className="text-sm font-medium">
                  Verified
                </label>
              </div>

              <PlacePicker
                latitude={editData.latitude}
                longitude={editData.longitude}
                googlePlaceId={editData.google_place_id}
                onSelect={(fields) => setEditData((d) => ({ ...d, ...fields }))}
              />

              <div>
                <label className="text-sm font-medium">URL</label>
                <input
                  type="url"
                  value={editData.url}
                  onChange={(e) => setEditData((d) => ({ ...d, url: e.target.value }))}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Address</label>
                <input
                  type="text"
                  value={editData.address}
                  onChange={(e) => setEditData((d) => ({ ...d, address: e.target.value }))}
                  className={inputClass}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="text-sm font-medium">City</label>
                  <input
                    type="text"
                    value={editData.city}
                    onChange={(e) => setEditData((d) => ({ ...d, city: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Country</label>
                  <input
                    type="text"
                    value={editData.country}
                    onChange={(e) => setEditData((d) => ({ ...d, country: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">State</label>
                  <input
                    type="text"
                    value={editData.state}
                    onChange={(e) => setEditData((d) => ({ ...d, state: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Main population</label>
                <div className="mt-1">
                  <CitySelect
                    value={editData.main_population || ""}
                    onChange={(value) => setEditData((d) => ({ ...d, main_population: value }))}
                    allLabel="Unassigned"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The city this venue actually serves, as opposed to its legal city above. Ingestion and
                  the backfill only fill it when empty, so a value set here survives them. Clearing it is
                  not durable: if the venue's coordinates fall inside a configured city's radius, the next
                  backfill assigns it again.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">Disposition</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {DISPOSITION_OPTIONS.map((opt) => {
                    const active = editData.disposition?.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setEditData((d) => ({ ...d, disposition: toggleInList(d.disposition, opt) }))
                        }
                        className={`px-3 py-1 rounded-md text-sm border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input"}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Modality</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {MODALITY_OPTIONS.map((opt) => {
                    const active = editData.modality?.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setEditData((d) => ({ ...d, modality: toggleInList(d.modality, opt) }))
                        }
                        className={`px-3 py-1 rounded-md text-sm border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input"}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="text-sm font-medium">Capacity</label>
                  <input
                    type="number"
                    min="0"
                    value={editData.capacity}
                    onChange={(e) => {
                      const val = e.target.value;
                      const inferred = inferCapacityBucketFromThresholds(val, capacityBuckets);
                      setEditData((d) => ({
                        ...d,
                        capacity: val,
                        ...(inferred ? { capacity_bucket: inferred } : {}),
                      }));
                    }}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Capacity bucket</label>
                  <select
                    value={editData.capacity_bucket}
                    onChange={(e) => setEditData((d) => ({ ...d, capacity_bucket: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="">Not set</option>
                    {CAPACITY_BUCKET_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {editData.capacity_bucket && (() => {
                    const b = capacityBuckets.find((x) => x.name === editData.capacity_bucket);
                    return b ? (
                      <p className="text-xs text-muted-foreground mt-1">{b.label}</p>
                    ) : null;
                  })()}
                </div>
                <div>
                  <label className="text-sm font-medium">Venue type hint</label>
                  <input
                    type="text"
                    value={editData.venue_type_hint}
                    onChange={(e) => setEditData((d) => ({ ...d, venue_type_hint: e.target.value }))}
                    placeholder="club, theater..."
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Wheelchair accessible</label>
                  <select
                    value={editData.wheelchair_accessible}
                    onChange={(e) => setEditData((d) => ({ ...d, wheelchair_accessible: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="">Not set</option>
                    {WHEELCHAIR_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Food & drinks</label>
                  <select
                    value={editData.food_and_drinks}
                    onChange={(e) => setEditData((d) => ({ ...d, food_and_drinks: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="">Not set</option>
                    {FOOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Public transport access</label>
                <textarea
                  value={editData.public_transport_access}
                  onChange={(e) => setEditData((d) => ({ ...d, public_transport_access: e.target.value }))}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Inferred context</label>
                <textarea
                  value={editData.venue_inferred_context}
                  onChange={(e) => setEditData((d) => ({ ...d, venue_inferred_context: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    value={editData.latitude}
                    onChange={(e) => setEditData((d) => ({ ...d, latitude: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    value={editData.longitude}
                    onChange={(e) => setEditData((d) => ({ ...d, longitude: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>

              <ModalFooter>
                <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setSelectedVenue(null); }} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Save</>
                  )}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <ModalContent title="Delete Venue">
          <p className="text-muted-foreground">
            Are you sure you want to delete the venue "{venueDisplayName(selectedVenue)}"?
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            If this venue is referenced by events, you will need to force delete.
          </p>
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteModalOpen(false); setSelectedVenue(null); }} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleDelete(true)} disabled={isDeleting}>
              Force delete
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(false)} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                "Delete"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </>)}
    </div>
  );
}
